// filepath: apps/web/src/features/admin/templates/registry/useTemplateInstall.ts
//
// FR-UI-ADM-11 AC-4 -- TanStack Query mutation hook for the
// install verb (POST /api/templates/{template_id}/install).
//
// Idempotency (Rule 3): `X-Idempotency-Key: crypto.randomUUID()`
// is sent on every invocation; the server replays the original
// response on a duplicate key.
//
// Staging-only (FR-UI-ADM-11 AC-6): a 403 ENV_PRODUCTION response
// maps to `TemplateInstallFetchError` of kind "staging_only".
// The route shell's environmentKind gate is the SPA-layer half;
// the handler's TenantFromContext check is the server-layer half.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError } from "@/definitions/types";
import { apiFetch } from "@/api";
import type {
  InstallRequest,
  InstallResponse,
  TemplateInstallFetchError,
} from "./types";
import { templateRegistryKeys } from "./useTemplateRegistry";

/**
 * useTemplateInstall -- POST /api/templates/{template_id}/install.
 *
 * Returns the typed union `TemplateInstallFetchError` on failure;
 * the InstallButton maps each kind to a typed error card.
 */
export function useTemplateInstall() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    InstallResponse,
    Error | TemplateInstallFetchError,
    { templateID: string } & InstallRequest
  >({
    mutationFn: async ({ templateID, template_version }) => {
      // ISS-377 round 2.1 (R2-B2): migrated to apiFetch. The
      // `idempotencyKey: true` form mints a fresh v4 UUID per
      // call (3-tier: randomUUID → getRandomValues → throw).
      const res = await apiFetch(
        `/api/templates/${encodeURIComponent(templateID)}/install`,
        {
          method: "POST",
          init: {
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ template_version }),
          },
          idempotencyKey: true,
        },
      );
      if (res.status === 403) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        if (body.code === "ENV_PRODUCTION") {
          const err: TemplateInstallFetchError = {
            kind: "staging_only",
            message: body.error ?? "template install is staging-only",
            retriable: false,
          };
          throw err;
        }
        const err: TemplateInstallFetchError = {
          kind: "permission_denied",
          message: body.error ?? "permission denied",
          retriable: false,
        };
        throw err;
      }
      if (res.status === 404) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        const err: TemplateInstallFetchError = {
          kind: "template_not_found",
          message: body.error ?? "template not found",
          retriable: false,
        };
        throw err;
      }
      if (res.status === 422) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        const err: TemplateInstallFetchError = {
          kind: "seeding_failed",
          message: body.error ?? "seeding failed",
          retriable: false,
        };
        throw err;
      }
      if (res.status === 429) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        const err: TemplateInstallFetchError = {
          kind: "rate_limit",
          message: body.error ?? "rate limited",
          retriable: true,
        };
        throw err;
      }
      if (!res.ok) {
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
      }
      return (await res.json()) as InstallResponse;
    },
    onSuccess: () => {
      // Refresh the registry cache (the new seed shows up after
      // promotion; for now the install lands in staging).
      void queryClient.invalidateQueries({
        queryKey: templateRegistryKeys.registry(tenantSlug),
      });
    },
  });
}
